import * as SentrySolid from "@sentry/solid";

import Log from "@repo/utils/Log";
import UObject from "@repo/utils/UObject";

namespace USentry {
  export const initSolid = ({
    beforeSend,
    tombstoneDeduperUrl,
    ...options
  }: UObject.Override<
    Parameters<typeof SentrySolid.init>[0],
    {
      beforeSend?: (context: {
        event: SentrySolid.ErrorEvent;
        hint: SentrySolid.EventHint;
        suppress: boolean;
        frames: SentrySolid.StackFrame[] | undefined;
      }) => ReturnType<NonNullable<SentrySolid.BrowserOptions["beforeSend"]>>;
      tombstoneDeduperUrl?: string;
      apiOrigin: string;
    }
  >) =>
    SentrySolid.init({
      integrations: [
        SentrySolid.browserTracingIntegration(),
        SentrySolid.replayIntegration(),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [options.apiOrigin],
      // Session Replays
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: true,
      beforeSend(event, hint) {
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;

        // prettier-ignore
        // @ts-expect-error
        Object.assign((event.tags ??= {}), hint.originalException?.tags);

        // prettier-ignore
        // @ts-expect-error
        Object.assign((event.extra ??= {}), hint.originalException?.extra);

        const suppress =
          !event.tags?.bypassSuppression &&
          // Suppress events without errors, we can't do much with these anyways
          (!frames?.some(
            ({ filename }) =>
              filename &&
              /^https?:\/\//.test(filename) &&
              // Ignore errors from entry html file, we don't have application code
              // there, but browsers can throw with this source
              /\.(js|ts|jsx|tsx|mts|mjs)$/.test(new URL(filename).pathname)
          ) ||
            // Suppress errors from 3rd party scripts
            frames.some(
              ({ filename }) =>
                filename?.startsWith("https://") &&
                !filename.startsWith(window.location.origin)
            ));

        const res = (() => {
          if (beforeSend) return beforeSend({ event, hint, suppress, frames });
          if (suppress) return null;
          return event;
        })();

        Log.withTrace`${Log.color("#f20", "Sentry event")} ${!res ? Log.style("background:green", " Suppressed ") : ""} -> ${event}`;

        if (tombstoneDeduperUrl && event.tags?.tombstone) {
          fetch(tombstoneDeduperUrl, {
            method: "POST",
            body: JSON.stringify({ event, dsn: options.dsn }),
          });

          return null;
        }

        return res;
      },
      ...options,
    });
}

export default USentry;
